import React, { useEffect, useState } from 'react'
import { Card, Table, Button, Select, InputNumber, Form, Row, Col, Divider, DatePicker, Slider, message } from 'antd'
import usePainMedication from '../hooks/usePainMedication'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'

const { Option } = Select

const PainMedicationManagement = ({ value = [], onChange }) => {
  const [form] = Form.useForm()
  // eslint-disable-next-line
  const { medications, painManagementOptions, loading } = usePainMedication()
  const [selectedMedications, setSelectedMedications] = useState(value)

  useEffect(() => {
    if (value && value.length > 0) {
      setSelectedMedications(value)
    }
  }, [value])

  const handleAddMedication = () => {
    form.validateFields().then((values) => {
      const selectedMed = medications.find((med) => med.id === values.medicationId)

      const newMedication = {
        id: values.medicationId,
        name: selectedMed?.name || '',
        dosage: values.dosage,
        unit: values.unit,
        frequency: values.frequency,
        duration: values.duration,
        startDate: values.startDate,
        painLevel: values.painLevel,
        instructions: values.instructions,
        sideEffects: selectedMed?.sideEffects || '',
        adherence: 0
      }

      const updatedMedications = [...selectedMedications, newMedication]
      setSelectedMedications(updatedMedications)
      onChange(updatedMedications)

      form.resetFields()
      message.success('Medication added successfully')
    })
  }

  const handleRemoveMedication = (medicationId) => {
    const updatedMedications = selectedMedications.filter((med) => med.id !== medicationId)
    setSelectedMedications(updatedMedications)
    onChange(updatedMedications)
    message.success('Medication removed successfully')
  }

  const columns = [
    {
      title: 'Medication',
      dataIndex: 'name',
      key: 'name',
      width: '15%'
    },
    {
      title: 'Dosage',
      key: 'dosage',
      width: '10%',
      render: (_, record) => `${record.dosage} ${record.unit}`
    },
    {
      title: 'Frequency',
      dataIndex: 'frequency',
      key: 'frequency',
      width: '12%'
    },
    {
      title: 'Duration',
      dataIndex: 'duration',
      key: 'duration',
      width: '12%'
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
      width: '15%',
      render: (date) => (date ? date.format('DD/MM/YYYY') : '')
    },
    {
      title: 'Pain Level',
      dataIndex: 'painLevel',
      key: 'painLevel',
      width: '10%',
      render: (level) => `${level}/10`
    },
    {
      title: 'Instructions',
      dataIndex: 'instructions',
      key: 'instructions',
      width: '20%'
    },
    {
      title: 'Action',
      key: 'action',
      width: '6%',
      render: (_, record) => (
        <Button icon={<DeleteOutlined />} danger type='link' onClick={() => handleRemoveMedication(record.id)} />
      )
    }
  ]

  return (
    <Card title='Medication & Pain Management' bordered={false}>
      <Form form={form} layout='vertical'>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name='medicationId'
              label='Medication'
              rules={[{ required: true, message: 'Please select a medication' }]}
            >
              <Select placeholder='Select medication' loading={loading} showSearch optionFilterProp='children'>
                {medications.map((medication) => (
                  <Option key={medication.id} value={medication.id}>
                    {medication.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name='dosage' label='Dosage' rules={[{ required: true, message: 'Enter dosage' }]}>
              <InputNumber min={1} max={1000} placeholder='Dosage' style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={3}>
            <Form.Item name='unit' label='Unit' rules={[{ required: true, message: 'Select unit' }]}>
              <Select placeholder='Unit'>
                <Option value='mg'>mg</Option>
                <Option value='ml'>ml</Option>
                <Option value='g'>g</Option>
                <Option value='tablets'>tablets</Option>
                <Option value='capsules'>capsules</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name='frequency' label='Frequency' rules={[{ required: true, message: 'Select frequency' }]}>
              <Select placeholder='Select frequency'>
                <Option value='Once daily'>Once daily</Option>
                <Option value='Twice daily'>Twice daily</Option>
                <Option value='Three times daily'>Three times daily</Option>
                <Option value='Four times daily'>Four times daily</Option>
                <Option value='Every 4-6 hours'>Every 4-6 hours</Option>
                <Option value='As needed for pain'>As needed for pain</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={5}>
            <Form.Item name='duration' label='Duration' rules={[{ required: true, message: 'Select duration' }]}>
              <Select placeholder='Select duration'>
                <Option value='3 days'>3 days</Option>
                <Option value='5 days'>5 days</Option>
                <Option value='7 days'>7 days</Option>
                <Option value='10 days'>10 days</Option>
                <Option value='14 days'>14 days</Option>
                <Option value='30 days'>30 days</Option>
                <Option value='As directed'>As directed</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={6}>
            <Form.Item
              name='startDate'
              label='Start Date'
              rules={[{ required: true, message: 'Please select a start date' }]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name='painLevel'
              label='Pain Level (Target)'
              rules={[{ required: true, message: 'Please select pain level' }]}
            >
              <Slider
                min={0}
                max={10}
                marks={{
                  0: 'No Pain',
                  3: '3',
                  5: 'Moderate',
                  7: '7',
                  10: 'Severe'
                }}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name='instructions'
              label='Instructions'
              rules={[{ required: true, message: 'Please add instructions' }]}
            >
              <Select placeholder='Select instructions'>
                <Option value='Take with food'>Take with food</Option>
                <Option value='Take on an empty stomach'>Take on an empty stomach</Option>
                <Option value='Take before physical therapy'>Take before physical therapy</Option>
                <Option value='Take at bedtime'>Take at bedtime</Option>
                <Option value='Do not drive after taking'>Do not drive after taking</Option>
                <Option value='Stop if experiencing severe side effects'>
                  Stop if experiencing severe side effects
                </Option>
                <Option value='Avoid alcohol while taking this medication'>
                  Avoid alcohol while taking this medication
                </Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row>
          <Col span={24} style={{ textAlign: 'right' }}>
            <Button type='primary' onClick={handleAddMedication} icon={<PlusOutlined />}>
              Add Medication
            </Button>
          </Col>
        </Row>
      </Form>

      <Divider />

      <Table columns={columns} dataSource={selectedMedications} rowKey='id' pagination={false} scroll={{ y: 300 }} />

      {selectedMedications.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          No medications added yet. Use the form above to add medications to the rehabilitation plan.
        </div>
      )}
    </Card>
  )
}

export default PainMedicationManagement
